import { z, type ZodType } from 'zod';

import { jsonRequest } from './shared';
import type { TipType } from './timekeeping';
import { getApiErrorCode } from '@/lib/api/errors';

const requestJson = async <T>(
  path: string,
  init: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown } = {},
): Promise<T> => {
  return jsonRequest<T>(path, {
    method: init.method ?? 'GET',
    body: init.body,
    headers: init.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    schema: z.any() as ZodType<T>,
  });
};

export interface PayrollOnboardingTask {
  id: string;
  taskType: string;
  status: string;
  assignedTo?: string | null;
  dueAt?: string | null;
  completedAt?: string | null;
}

export interface PayrollEmployeeRecord {
  id: string;
  legalName: string;
  preferredName?: string | null;
  email: string;
  phone?: string | null;
  address?: Record<string, unknown> | null;
  workLocationState: string;
  hireDate?: string | null;
  employmentType: string;
  ssnMasked?: string | null;
  tasks?: PayrollOnboardingTask[];
}

export interface PayrollEmployeeListResponse {
  ok: true;
  employees: PayrollEmployeeRecord[];
}

export interface PayrollEmployeeDetailResponse {
  ok: true;
  employee: PayrollEmployeeRecord & {
    tasks: PayrollOnboardingTask[];
  };
}

export const listPayrollEmployees = (): Promise<PayrollEmployeeListResponse> =>
  requestJson<PayrollEmployeeListResponse>('/api/payroll_bridge/employees');

export const getPayrollEmployee = (employeeId: string): Promise<PayrollEmployeeDetailResponse> =>
  requestJson<PayrollEmployeeDetailResponse>(`/api/payroll_bridge/employees/${employeeId}`);

export const invitePayrollEmployee = (payload: unknown): Promise<{ ok: true }> =>
  requestJson<{ ok: true }>('/api/payroll_bridge/onboarding/invite', {
    method: 'POST',
    body: payload,
  });

export const completePayrollOnboarding = (employeeId: string): Promise<{ ok: true; documentId: string }> =>
  requestJson<{ ok: true; documentId: string }>(`/api/payroll_bridge/onboarding/${employeeId}/complete`, {
    method: 'POST',
  });

export const submitPayrollW4 = (
  employeeId: string,
  payload: unknown,
): Promise<{ ok: true; documents: { federal: string; state: string | null } }> =>
  requestJson<{ ok: true; documents: { federal: string; state: string | null } }>(
    `/api/payroll_bridge/employees/${employeeId}/forms/w4`,
    {
      method: 'PUT',
      body: payload,
    },
  );

export const createPayrollChangeset = (payload: unknown): Promise<{ ok: true; changeset: unknown }> =>
  requestJson<{ ok: true; changeset: unknown }>('/api/payroll_bridge/changesets', {
    method: 'POST',
    body: payload,
  });

export const approvePayrollChangeset = (changesetId: string, payload: unknown): Promise<{ ok: true; documentId: string }> =>
  requestJson<{ ok: true; documentId: string }>(`/api/payroll_bridge/changesets/${changesetId}/approve`, {
    method: 'POST',
    body: payload,
  });

export const uploadPayrollDocument = (payload: unknown): Promise<{ ok: true; document: unknown }> =>
  requestJson<{ ok: true; document: unknown }>('/api/payroll_bridge/documents/upload', {
    method: 'POST',
    body: payload,
  });

export const unmaskPayrollSsn = (payload: unknown): Promise<{ ok: true; ssn: string }> =>
  requestJson<{ ok: true; ssn: string }>('/api/payroll_bridge/unmask/ssn', {
    method: 'POST',
    body: payload,
  });

export const sendPayrollOnboardingReminder = (
  employeeId: string,
): Promise<{ ok: true; reminder: { notificationId: string; pendingTaskCount: number; recipient: string } }> =>
  requestJson<{ ok: true; reminder: { notificationId: string; pendingTaskCount: number; recipient: string } }>(
    `/api/payroll_bridge/employees/${employeeId}/reminders`,
    {
      method: 'POST',
    },
  );

export type PayrollExportProviderKey = 'gusto' | 'quickbooks';
export type PayrollExportDeliveryMethod = 'download' | 'email' | 'sftp' | 'api';
export type PayrollExportGlFormat = 'quickbooks' | 'xero' | 'generic';
export type PayrollExportStatus =
  | 'pending'
  | 'validating'
  | 'failed_validation'
  | 'generating'
  | 'failed_generation'
  | 'delivering'
  | 'failed_delivery'
  | 'completed';
export type PayrollExportEventType =
  | 'export.requested'
  | 'validation.started'
  | 'validation.completed'
  | 'validation.failed'
  | 'generation.started'
  | 'generation.completed'
  | 'generation.failed'
  | 'delivery.started'
  | 'delivery.completed'
  | 'delivery.failed';

export interface PayrollExportProviderMeta {
  key: PayrollExportProviderKey;
  name: string;
  description: string;
  mappingVersion: string;
  supportedFileFormats: Array<'csv' | 'json'>;
  supportedGlFormats: PayrollExportGlFormat[];
  defaultDelivery: PayrollExportDeliveryMethod[];
  changePackSupport: boolean;
}

export interface PayrollExportDeliveryMeta {
  key: PayrollExportDeliveryMethod;
  label: string;
  description: string;
  requiresPayrollSync: boolean;
}

export interface PayrollExportGlMeta {
  key: PayrollExportGlFormat;
  label: string;
  description: string;
  fileType: 'csv' | 'iif';
}

export interface PayrollExportWizardConfigResponse {
  ok: true;
  config: {
    providers: PayrollExportProviderMeta[];
    deliveryMethods: PayrollExportDeliveryMeta[];
    glFormats: PayrollExportGlMeta[];
  };
}

export type PayrollExportValidationSeverity = 'error' | 'warning';

export interface PayrollExportValidationIssue {
  id: string;
  ruleId: string;
  severity: PayrollExportValidationSeverity;
  title: string;
  message: string;
  remediation?: string;
  affectedCount?: number;
  link?: string;
  impactedEmployeeIds?: string[];
}

export interface PayrollExportValidationSummary {
  blockingCount: number;
  warningCount: number;
  totalRulesEvaluated: number;
  employeesImpacted: number;
  readyEmployees: number;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalGrossPayCents: number;
  employeeCount: number;
}

export interface PayrollOwnerSummaryRange {
  from: string;
  to: string;
  days: number;
  label: string;
}

export interface PayrollOwnerAlert {
  id: string;
  category: 'labor' | 'compliance' | 'onboarding' | 'exports' | 'digest';
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'critical';
  ctaLabel?: string;
  ctaHref?: string;
}

export interface PayrollOwnerSummary {
  range: PayrollOwnerSummaryRange;
  metrics: {
    salesCents: number;
    estimatedLaborCostCents: number;
    laborPercent: number;
    laborPercentDelta: number | null;
    regularHours: number;
    overtimeHours: number;
    overtimeShare: number;
    premiumHours: number;
    totalTipsCents: number;
    averageTipsPerHourCents: number;
    tipCreditRiskLevel: 'low' | 'medium' | 'high';
    tipCreditFlaggedEmployees: number;
    employeeCount: number;
  };
  onboarding: {
    totalEmployees: number;
    pendingEmployees: number;
    completionRate: number;
    recentCompletions: number;
  };
  exports: {
    latestStatus: string | null;
    latestRunAt: string | null;
  };
  exceptions: {
    openCount: number;
  };
  alerts: PayrollOwnerAlert[];
}

export interface PayrollEssDashboard {
  employeeId: string;
  upcomingShifts: Array<{
    id: string;
    startAt: string;
    endAt: string;
    locationName: string | null;
    role: string | null;
  }>;
  periodSummaries: Array<{
    periodId: string;
    startDate: string;
    endDate: string;
    regularHours: number;
    overtimeHours: number;
    premiumHours: number;
    ptoHours: number;
    tipsCents: number;
    tipsByType: {
      cash: number;
      charged: number;
      serviceCharge: number;
    };
    tipDistributions: Array<{
      poolId: string;
      poolName: string;
      tipType: TipType;
      amountCents: number;
    }>;
    complianceFlags: string[];
  }>;
  accrualBalances: Array<{
    policyId: string;
    policyName: string;
    policyType: string;
    balanceHours: number;
    accrualRatePerHour: number;
  }>;
  ptoRequests: Array<{
    id: string;
    policyId: string;
    hours: number;
    startDate: string;
    endDate: string | null;
    status: 'pending' | 'approved' | 'denied';
    notes: string | null;
    submittedAt: string;
  }>;
  documentAcknowledgements: Array<{
    id: string;
    docType: string;
    signedAt: string | null;
    metadata: Record<string, unknown>;
  }>;
  alerts: Array<{
    id: string;
    title: string;
    message: string;
    severity: 'info' | 'warning';
  }>;
}

export interface PayrollPtoRequestPayload {
  policyId: string;
  hours: number;
  startDate: string;
  endDate?: string | null;
  notes?: string;
}

export interface PayrollPolicyAckPayload {
  policyKey: string;
  policyTitle: string;
  version: string;
  acknowledgedAt: string;
  signerIp?: string;
}

export interface PayrollNewHireStateConfig {
  stateCode: string;
  name: string;
  submissionDeadlineDays: number;
  portalUrl?: string | null;
  submissionMethods: Array<'portal' | 'fax' | 'mail' | 'api' | 'other'>;
  instructions: string[];
  notes?: string | null;
}

export interface PayrollNewHireReport {
  id: string;
  tenantId: string;
  employeeId: string;
  employeeName: string;
  employeeEmail: string | null;
  stateCode: string;
  status: 'pending' | 'generated' | 'submitted';
  submissionMethod: 'portal' | 'fax' | 'mail' | 'api' | 'other' | null;
  instructionsUrl: string | null;
  documentId: string | null;
  generatedAt: string;
  submittedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface PayrollNewHireReportCreatePayload {
  employeeId: string;
  stateCode: string;
  submissionMethod?: 'portal' | 'fax' | 'mail' | 'api' | 'other';
}

export interface PayrollNewHireReportSubmitPayload {
  method: 'portal' | 'fax' | 'mail' | 'api' | 'other';
  submittedAt?: string;
  reference?: string;
  notes?: string;
}

export interface PayrollEntitlements {
  planTier: 'bridge' | 'sync' | 'paid';
  features: Record<string, boolean>;
}

export interface PayrollExportPreviewResponse {
  ok: true;
  preview: {
    payPeriodId: string;
    providerKey: PayrollExportProviderKey;
    includeChangePacks: boolean;
    summary: PayrollExportValidationSummary;
    issues: PayrollExportValidationIssue[];
    recommendedDelivery: PayrollExportDeliveryMethod[];
  };
}

export interface PayrollExportValidationResult {
  summary: PayrollExportValidationSummary;
  issues: PayrollExportValidationIssue[];
}

export interface PayrollExportRecord {
  id: string;
  tenantId: string;
  payPeriodId: string | null;
  provider: PayrollExportProviderKey;
  status: PayrollExportStatus;
  includeChangePacks: boolean;
  deliveryMethods: PayrollExportDeliveryMethod[];
  glFormat: PayrollExportGlFormat | null;
  mappingVersion: string | null;
  requestedBy: string | null;
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  failureCode: string | null;
  failureMessage: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface PayrollExportEventRecord {
  id: string;
  exportId: string;
  tenantId: string;
  actorId: string | null;
  eventType: PayrollExportEventType;
  message: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface PayrollExportFileRecord {
  id: string;
  exportId: string;
  tenantId: string;
  fileType: 'payroll' | 'gl' | 'change_pack' | 'metadata';
  storageUri: string | null;
  filename: string | null;
  sizeBytes: number | null;
  checksum: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface PayrollExportCreateResponse {
  ok: true;
  export: PayrollExportRecord;
  files: PayrollExportFileRecord[];
  events: PayrollExportEventRecord[];
  validation: PayrollExportValidationResult;
  auditEvent: PayrollExportEventRecord;
}

export interface PayrollExportPreviewPayload {
  payPeriodId: string;
  providerKey: PayrollExportProviderKey;
  includeChangePacks?: boolean;
}

export interface PayrollExportCreatePayload extends PayrollExportPreviewPayload {
  delivery: PayrollExportDeliveryMethod[];
  glFormat?: PayrollExportGlFormat | null;
  emailRecipients?: string[];
}

export const getPayrollExportWizardConfig = (): Promise<PayrollExportWizardConfigResponse> =>
  requestJson<PayrollExportWizardConfigResponse>('/api/payroll_bridge/exports/config');

export const previewPayrollExport = (
  payload: PayrollExportPreviewPayload,
): Promise<PayrollExportPreviewResponse> =>
  requestJson<PayrollExportPreviewResponse>('/api/payroll_bridge/exports/preview', {
    method: 'POST',
    body: payload,
  });

export const createPayrollExport = (
  payload: PayrollExportCreatePayload,
): Promise<PayrollExportCreateResponse> =>
  requestJson<PayrollExportCreateResponse>('/api/payroll_bridge/exports', {
    method: 'POST',
    body: payload,
  });

export interface PayrollExportListResponse {
  ok: true;
  exports: PayrollExportRecord[];
}

export const listPayrollExports = (): Promise<PayrollExportListResponse> =>
  requestJson<PayrollExportListResponse>('/api/payroll_bridge/exports');

export interface PayrollExportDetailResponse {
  ok: true;
  export: PayrollExportRecord;
  files: PayrollExportFileRecord[];
  events: PayrollExportEventRecord[];
}

export const getPayrollExportDetail = (
  exportId: string,
): Promise<PayrollExportDetailResponse> =>
  requestJson<PayrollExportDetailResponse>(`/api/payroll_bridge/exports/${exportId}`);

export const fetchPayrollOwnerSummary = async (range?: string): Promise<PayrollOwnerSummary> => {
  const query = range ? `?range=${encodeURIComponent(range)}` : '';
  const response = await requestJson<{ ok: true; summary: PayrollOwnerSummary }>(
    `/api/payroll_bridge/owner/summary${query}`,
  );
  return response.summary;
};

export const sendPayrollPrepDigest = (): Promise<{
  ok: true;
  result: { recipients: number; notificationIds: string[] };
}> =>
  requestJson('/api/payroll_bridge/digest/send', {
    method: 'POST',
  });

export const fetchPayrollEssDashboard = async (employeeId: string): Promise<PayrollEssDashboard> => {
  const response = await requestJson<{ ok: true; dashboard: PayrollEssDashboard }>(
    `/api/payroll_bridge/employees/${employeeId}/ess/dashboard`,
  );
  return response.dashboard;
};

export const submitPayrollPtoRequest = (
  employeeId: string,
  payload: PayrollPtoRequestPayload,
): Promise<{ ok: true; requestId: string }> =>
  requestJson<{ ok: true; requestId: string }>(
    `/api/payroll_bridge/employees/${employeeId}/pto-requests`,
    {
      method: 'POST',
      body: payload,
    },
  );

export const acknowledgePayrollPolicy = (
  employeeId: string,
  payload: PayrollPolicyAckPayload,
): Promise<{ ok: true; documentId: string }> =>
  requestJson<{ ok: true; documentId: string }>(
    `/api/payroll_bridge/employees/${employeeId}/policy-ack`,
    {
      method: 'POST',
      body: payload,
    },
  );

export const fetchPayrollNewHireConfig = async (): Promise<PayrollNewHireStateConfig[]> => {
  const response = await requestJson<{ ok: true; states: PayrollNewHireStateConfig[] }>(
    '/api/payroll_bridge/new_hire/config',
  );
  return response.states;
};

export const listPayrollNewHireReports = async (): Promise<PayrollNewHireReport[]> => {
  const response = await requestJson<{ ok: true; reports: PayrollNewHireReport[] }>(
    '/api/payroll_bridge/new_hire/reports',
  );
  return response.reports;
};

export const createPayrollNewHireReport = (
  payload: PayrollNewHireReportCreatePayload,
): Promise<{ ok: true; report: PayrollNewHireReport }> =>
  requestJson<{ ok: true; report: PayrollNewHireReport }>(
    '/api/payroll_bridge/new_hire/reports',
    {
      method: 'POST',
      body: payload,
    },
  );

export const submitPayrollNewHireReport = (
  reportId: string,
  payload: PayrollNewHireReportSubmitPayload,
): Promise<{ ok: true; report: PayrollNewHireReport }> =>
  requestJson<{ ok: true; report: PayrollNewHireReport }>(
    `/api/payroll_bridge/new_hire/reports/${reportId}/submit`,
    {
      method: 'POST',
      body: payload,
    },
  );

export const fetchPayrollEntitlements = async (): Promise<PayrollEntitlements> => {
  const response = await requestJson<{ ok: true; entitlements: PayrollEntitlements }>(
    '/api/payroll_bridge/entitlements',
  );
  return response.entitlements;
};

export const trackPayrollEntitlementEvent = (
  payload: { event: string; metadata?: Record<string, unknown> },
): Promise<{ ok: true }> =>
  requestJson<{ ok: true }>('/api/payroll_bridge/entitlements/track', {
    method: 'POST',
    body: payload,
  });

export const verifyPayrollMfa = (code: string): Promise<void> =>
  requestJson<void>('/api/payroll_bridge/mfa/verify', {
    method: 'POST',
    body: { code },
  });

export const isPayrollMfaError = (error: unknown): boolean =>
  getApiErrorCode(error) === 'MFA_REQUIRED';
